import type {
  Claim,
  Debit,
  Organization,
  Payor,
  Role,
  Submission,
} from 'interfaces';

export type FixtureRecord = Record<string, any>;

export type CanonicalRole = Role & { organization: Organization };
export type CanonicalSubmission = Submission & {
  payor?: Payor | null;
  claims: Claim[];
};
export type CanonicalDebit = Debit & { eobs: NonNullable<Debit['eobs']> };
export type CanonicalClaim = Claim & { debits: CanonicalDebit[] };

const hasOverride = <T extends FixtureRecord>(
  overrides: Partial<T>,
  key: keyof T
) => Object.prototype.hasOwnProperty.call(overrides, key);

export const normalizeSite = <T extends FixtureRecord>(site: T): T => ({
  ...site,
  organizationID: site.organization?.id ?? site.organizationID ?? null,
});

export const normalizeRole = <T extends FixtureRecord>(role: T): T => ({
  ...role,
  organizationID: role.organization?.id ?? role.organizationID ?? null,
});

export const normalizeUser = <T extends FixtureRecord>(
  user: T,
  providerByUserID: Map<string, FixtureRecord[]>
): T => {
  const providers = providerByUserID.get(user.id) ?? user.providers ?? [];

  return {
    ...user,
    organizationID: user.organization?.id ?? user.organizationID ?? null,
    roleID: user.role?.id ?? user.roleID ?? null,
    providers,
    providerID: providers[0]?.id ?? user.providerID ?? null,
  };
};

export const normalizeProvider = <T extends FixtureRecord>(provider: T): T => ({
  ...provider,
  siteID: provider.sites?.[0]?.id ?? provider.siteID ?? null,
  userID: provider.user?.id ?? provider.userID ?? null,
});

export const normalizePatient = <T extends FixtureRecord>(
  patient: T,
  healthcards: FixtureRecord[]
): T => ({
  ...patient,
  organizationID: patient.organization?.id ?? patient.organizationID ?? null,
  healthcards,
  healthcardID: healthcards[0]?.id ?? patient.healthcardID ?? null,
});

export const normalizeHealthcard = <T extends FixtureRecord>(
  healthcard: T
): T => ({
  ...healthcard,
  patientID: healthcard.patient?.id ?? healthcard.patientID ?? null,
});

export const normalizeSubmission = <T extends FixtureRecord>(
  submission: T
): T => ({
  ...submission,
  siteID: submission.site?.id ?? submission.siteID ?? null,
  payorID: submission.payor?.id ?? submission.payorID ?? null,
  claims: submission.claims ?? [],
});

export const normalizeClaim = <T extends FixtureRecord>(claim: T): T => ({
  ...claim,
  patientID: claim.patient?.id ?? claim.patientID ?? null,
  providerID: claim.provider?.id ?? claim.providerID ?? null,

  siteID: claim.site?.id ?? claim.siteID ?? null,
  healthcardID: claim.healthcard?.id ?? claim.healthcardID ?? null,
  payorID: claim.payor?.id ?? claim.payorID ?? null,
  submissions: claim.submissions ?? [],
  debits: (claim.debits ?? []).map((debit: FixtureRecord) => ({
    ...debit,
    claimID: claim.id,
    payorID: debit.payor?.id ?? claim.payor?.id ?? debit.payorID ?? null,
    providerID:
      debit.provider?.id ?? claim.provider?.id ?? debit.providerID ?? null,
    eobs: debit.eobs ?? [],
  })),
  claimHistories: claim.claimHistories ?? [],
  actionRequests: claim.actionRequests ?? [],
  actionResponses: claim.actionResponses ?? [],
});

export const deriveFixtureForeignKeys = <T extends FixtureRecord>(
  base: T,
  overrides: Partial<T> = {}
): T => {
  const fixture: FixtureRecord = { ...base, ...overrides };

  if (
    fixture.organization?.id &&
    !hasOverride(overrides, 'organizationID' as keyof T)
  ) {
    fixture.organizationID = fixture.organization.id;
  }

  if (fixture.role?.id && !hasOverride(overrides, 'roleID' as keyof T)) {
    fixture.roleID = fixture.role.id;
  }

  if (
    Array.isArray(fixture.providers) &&
    fixture.providers[0]?.id &&
    !hasOverride(overrides, 'providerID' as keyof T)
  ) {
    fixture.providerID = fixture.providers[0].id;
  }

  if (fixture.user?.id && !hasOverride(overrides, 'userID' as keyof T)) {
    fixture.userID = fixture.user.id;
  }

  if (
    Array.isArray(fixture.sites) &&
    fixture.sites[0]?.id &&
    !hasOverride(overrides, 'siteID' as keyof T)
  ) {
    fixture.siteID = fixture.sites[0].id;
  }

  if (
    Array.isArray(fixture.healthcards) &&
    fixture.healthcards[0]?.id &&
    !hasOverride(overrides, 'healthcardID' as keyof T)
  ) {
    fixture.healthcardID = fixture.healthcards[0].id;
  }

  if (fixture.patient?.id && !hasOverride(overrides, 'patientID' as keyof T)) {
    fixture.patientID = fixture.patient.id;
  }

  if (
    fixture.healthcard?.id &&
    !hasOverride(overrides, 'healthcardID' as keyof T)
  ) {
    fixture.healthcardID = fixture.healthcard.id;
  }

  if (fixture.payor?.id && !hasOverride(overrides, 'payorID' as keyof T)) {
    fixture.payorID = fixture.payor.id;
  }

  if (fixture.site?.id && !hasOverride(overrides, 'siteID' as keyof T)) {
    fixture.siteID = fixture.site.id;
  }

  if (
    fixture.provider?.id &&
    !hasOverride(overrides, 'providerID' as keyof T)
  ) {
    fixture.providerID = fixture.provider.id;
  }

  if (Array.isArray(fixture.debits)) {
    fixture.debits = fixture.debits.map((debit: FixtureRecord) => ({
      ...debit,
      claimID: debit.claimID ?? fixture.id ?? null,
      payorID: debit.payor?.id ?? fixture.payor?.id ?? debit.payorID ?? null,
      providerID:
        debit.provider?.id ?? fixture.provider?.id ?? debit.providerID ?? null,
      eobs: debit.eobs ?? [],
    }));
  }

  return fixture as T;
};
